import { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import { getRoleHomeUrl } from 'constants/routes';
import {
  QuestionnaireCandidateStatus,
  useQuestionnaireStageCandidateQuery,
} from 'generated/graphql';

/**
 * Loads the eligibility (questionnaire) stage for the intro and questions
 * pages, and bounces stages that were already finished on arrival back to
 * the portal — answers are write-once, so a passed or failed stage has
 * nothing to show; the portal renders the verdict.
 *
 * Only the status seen on entry counts: submitting writes the new status
 * into the cache straight away, and the questions page owns that exit (it
 * holds for the "Calculating..." delay before navigating).
 */
export function useEligibilityStage() {
  const navigate = useNavigate();
  const { handleMsgType } = useNotification();
  const { roleId, stageId } = useParams();

  const { data, loading } = useQuestionnaireStageCandidateQuery({
    variables: {
      roleId: roleId as string,
      stageId: stageId as string,
    },
    skip: !roleId || !stageId,
    onError: error => {
      handleMsgType({
        type: TNotification.error,
        message: error?.message,
      });
    },
    fetchPolicy: 'network-only',
  });

  const role = data?.myRole;
  const stage = role?.questionnaireStages[0];

  const backUrl = getRoleHomeUrl(
    roleId as string,
    role?.candidatePortalEnabled
  );

  const finishedOnEntry = useRef<boolean | null>(null);
  if (stage && finishedOnEntry.current === null) {
    finishedOnEntry.current =
      stage.status === QuestionnaireCandidateStatus.Completed ||
      stage.status === QuestionnaireCandidateStatus.Failed;
  }
  const isAlreadyFinished = finishedOnEntry.current === true;
  useEffect(() => {
    if (isAlreadyFinished) {
      navigate(backUrl, { replace: true });
    }
  }, [isAlreadyFinished, backUrl, navigate]);

  return {
    roleId: roleId as string,
    stageId: stageId as string,
    role,
    stage,
    loading,
    backUrl,
  };
}
